/**
 * Resolve, a partir dos campos configurados no formulário do cliente
 * (`form_fields`), o RÓTULO DA PERGUNTA e o TEXTO DA RESPOSTA que o denunciante
 * efetivamente viu e escolheu — em vez de exibir a chave/valor crus do banco.
 *
 * Motivação: campos de opção guardam um "valor interno" que pode ser
 * semanticamente diferente do texto exibido. Ex.: na mauad, o valor gravado
 * `diretores` corresponde à opção "Jurídico"; na galena, a "Comitê de Ética".
 * Mostrar o valor cru na triagem/exportação exibe informação enganosa.
 */

/** Normaliza uma opção (string simples ou {value,label}) para {value,label}. */
const normalizeOption = (option) => {
  if (option && typeof option === 'object') {
    const value = option.value !== undefined ? option.value : option.label;
    const label = option.label !== undefined ? option.label : option.value;
    return { value, label };
  }
  return { value: option, label: option };
};

const eq = (a, b) => String(a ?? '').trim() === String(b ?? '').trim();

/** Encontra a definição do campo pelo nome (tolerante a aliases do sobre_quem). */
export const findField = (formFields, name) => {
  if (!Array.isArray(formFields)) return null;
  const direct = formFields.find((f) => f && f.name === name);
  if (direct) return direct;
  // sobre_quem já apareceu com nomes alternativos em formulários antigos.
  if (['sobre_quem', 'sobreQuem', 'sobre_quem_denuncia'].includes(name)) {
    return formFields.find((f) => f && ['sobre_quem', 'sobreQuem', 'sobre_quem_denuncia'].includes(f.name)) || null;
  }
  return null;
};

/**
 * Campo que divide a visualização entre os admins do cliente (espelha o
 * backend): o marcado com `divide_visualizacao: true` (unidade, filial...);
 * sem marca, o sobre_quem, a menos que desmarcado (`false`). null = sem divisão.
 */
export const findRoutingField = (formFields) => {
  if (!Array.isArray(formFields)) return null;
  const marcado = formFields.find((f) => f && f.divide_visualizacao === true);
  if (marcado) return marcado;
  const sobreQuem = findField(formFields, 'sobre_quem');
  return sobreQuem && sobreQuem.divide_visualizacao !== false ? sobreQuem : null;
};

/**
 * Texto da opção do campo que divide a visualização para um nível de acesso
 * (o valor gravado em admin_levels / complaints.sobre_quem). O slug não diz
 * nada sozinho: na atomplast, `presidencia` é "Recursos Humanos/Supervisão PCP"
 * e `diretores` é "CEO/Presidente". null quando o formulário não tem a opção.
 */
export const routingOptionLabel = (formFields, value) => {
  const field = findRoutingField(formFields);
  const options = Array.isArray(field?.options) ? field.options.map(normalizeOption) : [];
  const match = options.find((o) => eq(o.value, value));
  return match ? String(match.label ?? match.value) : null;
};

/** Rótulo da pergunta conforme o formulário; sem ele, humaniza a chave. */
export const getQuestionLabel = (formFields, name) => {
  const field = findField(formFields, name);
  if (field && field.label) return field.label;
  return String(name || '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

/**
 * Texto da resposta conforme as opções do formulário.
 * - Casa tanto pelo `value` quanto pelo `label` (dados antigos gravaram ambos).
 * - Suporta múltipla escolha (array ou string "a, b").
 * - Sem campo/opções correspondentes, devolve o valor original inalterado.
 */
export const resolveAnswer = (formFields, name, value) => {
  if (value === null || value === undefined || value === '') return value;

  const field = findField(formFields, name);
  const options = Array.isArray(field?.options) ? field.options.map(normalizeOption) : [];
  if (!options.length) return value;

  const labelFor = (v) => {
    const match = options.find((o) => eq(o.value, v) || eq(o.label, v));
    return match ? String(match.label ?? match.value) : null;
  };

  if (Array.isArray(value)) {
    return value.map((v) => labelFor(v) ?? v).join(', ');
  }

  const direct = labelFor(value);
  if (direct !== null) return direct;

  // Múltipla escolha serializada como "a, b": só reescreve se TODAS as partes
  // casarem, para não estragar texto livre que por acaso contenha vírgula.
  const parts = String(value).split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length > 1) {
    const mapped = parts.map(labelFor);
    if (mapped.every((m) => m !== null)) return mapped.join(', ');
  }

  return value;
};
